import { useRef } from "react"
import { Search, Upload, FileCheck2 } from "lucide-react"
import useNumberInput from "../hooks/useNumberInput"

// Lightweight, accessible form controls built straight from the design tokens —
// native <input>/<select>/<textarea> so keyboard focus, tab order and mobile
// pickers all work for free, with a visible focus ring from tokens.css.

export const Field = ({ label, required, hint, error, htmlFor, children }) => (
  <div className="pc-field">
    {label && (
      <label className="pc-label" htmlFor={htmlFor}>
        {label}
        {required && <span className="pc-req">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <span className="pc-hint pc-hint--error" role="alert">
        {error}
      </span>
    ) : (
      hint && <span className="pc-hint">{hint}</span>
    )}
  </div>
)

let idSeq = 0
const useId = (id) => id || `pc-f-${(idSeq += 1)}`

export const Input = ({ label, required, hint, error, id, className = "", ...rest }) => {
  const fid = useId(id)
  return (
    <Field label={label} required={required} hint={hint} error={error} htmlFor={fid}>
      {/* Deliberate pass-through — callers rely on forwarding arbitrary
          native <input> attributes (type, value, onChange, min, max,
          placeholder, disabled, list...) across the whole procurement UI. */}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <input id={fid} className={`pc-control ${className}`} aria-invalid={!!error || undefined} {...rest} />
    </Field>
  )
}

// Bare <input> with thousands separators. `onChange` receives the raw string.
export const NumberControl = ({ value, onChange, ...rest }) => {
  const num = useNumberInput(value, onChange)
  return (
    <input
      inputMode="decimal"
      // eslint-disable-next-line react/jsx-props-no-spreading -- see Input above
      {...rest}
      ref={num.ref}
      value={num.value}
      onChange={num.onChange}
      onCompositionStart={num.onCompositionStart}
      onCompositionEnd={num.onCompositionEnd}
    />
  )
}

export const NumberInput = ({ label, required, hint, error, id, className = "", ...rest }) => {
  const fid = useId(id)
  return (
    <Field label={label} required={required} hint={hint} error={error} htmlFor={fid}>
      <NumberControl id={fid} className={`pc-control ${className}`} aria-invalid={!!error || undefined} {...rest} />
    </Field>
  )
}

export const Textarea = ({ label, required, hint, error, id, rows = 3, ...rest }) => {
  const fid = useId(id)
  return (
    <Field label={label} required={required} hint={hint} error={error} htmlFor={fid}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading -- see Input above */}
      <textarea id={fid} rows={rows} className="pc-control" aria-invalid={!!error || undefined} {...rest} />
    </Field>
  )
}

// options: [{ value, label }]. `placeholder` renders a disabled first row.
export const Select = ({
  label,
  required,
  hint,
  error,
  id,
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  disabled,
  allowEmpty = true,
}) => {
  const fid = useId(id)
  return (
    <Field label={label} required={required} hint={hint} error={error} htmlFor={fid}>
      <select
        id={fid}
        className="pc-control"
        aria-invalid={!!error || undefined}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
      >
        {allowEmpty && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  )
}

// Search box with a leading magnifier icon.
export const SearchInput = ({ label, id, ...rest }) => {
  const fid = useId(id)
  return (
    <Field label={label} htmlFor={fid}>
      <div className="pc-input-wrap">
        <Search className="pc-lead" aria-hidden />
        {/* eslint-disable-next-line react/jsx-props-no-spreading -- see Input above */}
        <input id={fid} className="pc-control pc-has-lead" type="search" {...rest} />
      </div>
    </Field>
  )
}

// Filter/tool bar: composes a search box + selects + an optional reset. Keeps
// the three list screens (Order Request, My Orders, Budget) visually identical.
export const Toolbar = ({ children }) => (
  <div className="pc-toolbar">{children}</div>
)

// A single document upload row, styled from the tokens (used by the Order
// Request form's Attach Documents section).
export const FileSlot = ({ label, file, onSelect, disabled }) => {
  const ref = useRef(null)
  const name = file?.name || file?.fileName
  return (
    <div className={`pc-fileslot${name ? " pc-fileslot--filled" : ""}`}>
      <div className="pc-fileslot-main">
        <FileCheck2 size={18} aria-hidden className="pc-fileslot-ico" />
        <div style={{ minWidth: 0 }}>
          <div className="pc-fileslot-label">{label}</div>
          {name && <div className="pc-fileslot-name">{name}</div>}
        </div>
      </div>
      <button
        type="button"
        className="pc-btn pc-btn--secondary pc-btn--sm"
        disabled={disabled}
        onClick={() => ref.current?.click()}
      >
        <Upload size={15} aria-hidden />
        {name ? "Replace" : "Upload"}
      </button>
      <input
        ref={ref}
        type="file"
        hidden
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f && onSelect) onSelect(f)
          e.target.value = ""
        }}
      />
    </div>
  )
}
